import { fetcher } from '@commenda/utils';
import { ErrorCode } from 'react-dropzone';
import * as XLSX from 'xlsx';

export type SignedUrlForPutResponse = {
  // The URL associated with this file to save in the database for future GET requests.
  hostedUrl: string;
  // URL to POST to to push this file to S3.
  signedUrl: string;
  // Signed URL to GET this file from S3.
  signedGetUrl: string;
};

type SignedUrlForPutRequest = {
  fileName: string;
  companyId?: number;
};

export type GetSignedUrlForPut = (
  sanitizedFileName: string
) => Promise<SignedUrlForPutResponse | null>;

export const getSignedUrlForPut = async (
  sanitizedFileName: string,
  companyId: number | undefined,
  nextAuthToken: string | undefined,
  clerkToken?: string | undefined
): Promise<SignedUrlForPutResponse | null> => {
  try {
    const req: SignedUrlForPutRequest = {
      fileName: sanitizedFileName,
    };
    if (companyId) {
      req.companyId = companyId;
    }

    const res = await fetcher(
      `s3/signedUrlForPut`,
      {
        method: 'POST',
        body: req,
      },
      nextAuthToken || undefined,
      clerkToken || undefined
    );

    if (!res.ok) {
      console.error(await res.json());
      return null;
    }

    const data = await res.json();

    return data;
  } catch (err) {
    console.error(err);
    return null;
  }
};

/**
 * @deprecated This function is being removed due to security concerns (SEC-19).
 * The underlying endpoint `/s3/signedUrlForGetFromUrl` does not properly authorize file access.
 *
 * Instead, use one of these approaches:
 * 1. Pass `signedUrl` directly if available (e.g., immediately after upload)
 * 2. Pass `file.id` and use `getSignedUrlByFileId()` from '@commenda/utils/files/uploadFile'
 *    which calls the authorized endpoint `/s3/signedUrlForGet?fileId=X`
 *
 * This function now returns null and will be removed entirely in a future release.
 */
export const getSignedUrlForHostedFileUrl = async (
  _fileUrl: string,
  _corporationId: number,
  _clerkToken?: string
): Promise<string | null> => {
  console.warn(
    '[DEPRECATED] getSignedUrlForHostedFileUrl is deprecated and no longer functional. ' +
      'Use signedUrl directly or fetch via fileId using getSignedUrlByFileId(). ' +
      'See Linear SEC-73 for details.'
  );
  return null;
};

export const getSignedS3PutUrl = async (
  fileName: string,
  companyId: number | undefined,
  token: string | undefined,
  clerkToken?: string | undefined,
  signUrlForPut?: GetSignedUrlForPut
) => {
  const sanitizedFileName = fileName.replaceAll(/[^a-zA-Z0-9!\-_.*'()]/g, '_');

  const res = signUrlForPut
    ? await signUrlForPut(sanitizedFileName)
    : await getSignedUrlForPut(sanitizedFileName, companyId, token, clerkToken);

  if (!res) {
    console.error('Failed to get signed url ');
    return null;
  }

  return {
    signedUrl: res.signedUrl as string,
    url: res.hostedUrl as string,
    signedGetUrl: res.signedGetUrl as string,
  };
};

export const pushFileToS3Url = (signedRequest: string, file: File) => {
  const myHeaders = new Headers({
    'Content-Type': file.type || 'application/octet-stream',
  });

  return fetch(signedRequest, {
    method: 'PUT',
    headers: myHeaders,
    body: file,
  });
};

export async function uploadFileToS3(
  file: File,
  companyId: number | undefined,
  token: string | undefined,
  clerkToken?: string | undefined,
  signUrlForPut?: GetSignedUrlForPut
): Promise<{ fileName: string; fileUrl: string; downloadUrl: string } | null> {
  console.log({ clerkToken });

  const response = await getSignedS3PutUrl(
    file.name,
    companyId,
    token,
    clerkToken,
    signUrlForPut
  );
  if (!response) return null;

  const { signedUrl, url, signedGetUrl } = response;

  if (!signedUrl) {
    return null;
  }

  const saveFileResult = await pushFileToS3Url(signedUrl, file);

  if (!saveFileResult.ok) {
    console.error('Failed to save file to S3 URL');
    return null;
  }

  const fileData = {
    fileName: file.name,
    fileUrl: url,
    downloadUrl: signedGetUrl,
  };

  return fileData;
}

export const formatBytesString = (bytes: number | null): string => {
  if (!bytes) return '';

  const units: [string, number][] = [
    ['B', 0],
    ['KB', 10],
    ['MB', 20],
    ['GB', 30],
    ['TB', 40],
  ];

  const size = Math.abs(bytes);

  for (let i = units.length - 1; i >= 0; i -= 1) {
    const [unit, power] = units[i];
    if (size >= 2 ** power) {
      return `${(size / 2 ** power).toFixed(2)} ${unit}`;
    }
  }

  return '0 B';
};

export const getErrorMessage = (code: ErrorCode, message: string): string => {
  switch (code) {
    case ErrorCode.FileInvalidType:
      return 'Invalid file type. Please upload a supported file format.';
    case ErrorCode.FileTooLarge:
      return 'File is too large. Please upload a smaller file.';
    case ErrorCode.FileTooSmall:
      return 'File is too small. Please upload a larger file.';
    case ErrorCode.TooManyFiles:
      return 'Too many files. Please upload only one file.';
    default:
      return message || 'An error occurred while uploading the file.';
  }
};

export const readCSVColumns = (file: File): Promise<string[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        const firstLine = text.split('\n')[0];
        const columns = firstLine
          .split(',')
          .map((col) => col.trim().replace(/["']/g, ''));

        resolve(columns);
      } catch (err) {
        reject(new Error('Failed to parse CSV headers'));
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};

export const readExcelColumns = (file: File): Promise<string[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const data = event.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // Get the range of the worksheet
        const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
        const columns: string[] = [];

        // Read only the first row (headers)
        for (let col = range.s.c; col <= range.e.c; col += 1) {
          const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
          const cell = worksheet[cellAddress];
          columns.push(cell?.v?.trim() || '');
        }

        resolve(columns);
      } catch (err) {
        reject(new Error('Failed to parse Excel headers'));
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsBinaryString(file);
  });
};

export const readFileColumns = async (file: File): Promise<string[]> => {
  const fileType = file.name.split('.').pop()?.toLowerCase();

  switch (fileType) {
    case 'csv':
      return readCSVColumns(file);
    case 'xlsx':
    case 'xls':
      return readExcelColumns(file);
    default:
      throw new Error(
        'Unsupported file type. Please upload a CSV or Excel file.'
      );
  }
};
